import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import lucaCaffiSignature from "../assets/lucaCaffiSignature";
import { openPdfDocument } from "../utils/openPdfDocument";
import { generateVerbaleVerificaFinalePdf } from "../utils/verbaleVerificaFinalePdf";

const initialTest = {
  questionCount: "",
  threshold: "",
  testType: "",
  testLocation: "",
  documentPlace: "",
};

const participantKey = (participant) =>
  participant.Lavoratore ?? participant.ID;

const clampToRange = (value, minimum, maximum) => {
  if (value === "") return "";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;
  if (numericValue < minimum) return String(minimum);
  if (Number.isFinite(maximum) && numericValue > maximum) {
    return String(maximum);
  }
  return value;
};

export default function VerbaleVerificaFinaleDialog({
  open,
  onClose,
  course,
  courseType,
  regulatoryReference,
  deliveryMode,
  sessions,
  participants,
  onError,
}) {
  const [test, setTest] = useState(initialTest);
  const [participantValues, setParticipantValues] = useState([]);

  const lastCourseDate = useMemo(() => {
    const dates = (sessions || [])
      .map((session) => moment(session.Data))
      .filter((date) => date.isValid());
    if (!dates.length) return null;
    return moment.max(dates);
  }, [sessions]);

  useEffect(() => {
    if (!open) return;
    setTest({
      ...initialTest,
      testLocation: "",
      documentPlace: "",
    });
    setParticipantValues(
      (participants || []).map((participant) => ({
        id: participantKey(participant),
        name: [participant.Cognome, participant.Nome].filter(Boolean).join(" "),
        minimumAttendance: "yes",
        score: "",
        notes: "",
      })),
    );
  }, [open, participants]);

  const updateTest = (field, value) =>
    setTest((current) => ({ ...current, [field]: value }));

  const updateQuestionCount = (value) => {
    const normalizedValue = clampToRange(value, 1, Infinity);
    const maximum = normalizedValue === "" ? Infinity : Number(normalizedValue);

    setTest((current) => ({
      ...current,
      questionCount: normalizedValue,
      threshold: Number.isFinite(maximum)
        ? clampToRange(current.threshold, 0, maximum)
        : current.threshold,
    }));
    if (Number.isFinite(maximum)) {
      setParticipantValues((current) =>
        current.map((participant) => ({
          ...participant,
          score: clampToRange(participant.score, 0, maximum),
        })),
      );
    }
  };

  const updateParticipant = (index, field, value) => {
    setParticipantValues((current) =>
      current.map((participant, participantIndex) =>
        participantIndex === index
          ? { ...participant, [field]: value }
          : participant,
      ),
    );
  };

  const generatePdf = () => {
    const questionCount = Number(test.questionCount);
    const threshold = Number(test.threshold);
    const missingTestData =
      !test.questionCount.trim() || !test.threshold.trim() || !test.testType;
    const invalidTestData =
      !Number.isInteger(questionCount) ||
      questionCount <= 0 ||
      !Number.isFinite(threshold) ||
      threshold < 0 ||
      threshold > questionCount;
    const invalidParticipant = participantValues.some((participant) => {
      const score = Number(participant.score);
      return (
        participant.score === "" ||
        !Number.isFinite(score) ||
        score < 0 ||
        score > questionCount
      );
    });

    if (!participantValues.length) {
      onError("Non ci sono partecipanti iscritti al corso");
      return;
    }
    if (missingTestData) {
      onError("Compila numero domande, soglia e tipologia del test");
      return;
    }
    if (invalidTestData) {
      onError(
        "Inserisci valori validi: la soglia non può superare il numero di domande",
      );
      return;
    }
    if (invalidParticipant) {
      onError(
        "Inserisci per tutti un punteggio compreso tra 0 e il numero di domande",
      );
      return;
    }

    const sortedSessions = [...(sessions || [])].sort(
      (a, b) => moment(a.Data).valueOf() - moment(b.Data).valueOf(),
    );
    const firstDate = sortedSessions.length
      ? moment(sortedSessions[0].Data)
      : null;
    const lastDate = sortedSessions.length
      ? moment(sortedSessions[sortedSessions.length - 1].Data)
      : null;
    const duration = sortedSessions.reduce(
      (total, session) => total + Number(session.Durata || 0),
      0,
    );
    const period =
      firstDate?.isValid() && lastDate?.isValid()
        ? firstDate.isSame(lastDate, "day")
          ? firstDate.format("DD/MM/YYYY")
          : `${firstDate.format("DD/MM/YYYY")} - ${lastDate.format("DD/MM/YYYY")}`
        : "";

    const doc = generateVerbaleVerificaFinalePdf(
      {
        courseTitle: course?.Descrizione,
        trainingProvider: "Miller Sicurezza S.r.l.",
        regulatoryReference:
          regulatoryReference || courseType?.RiferimentoNormativo,
        courseLocation: course?.Luogo,
        durationAndPeriod: `${duration || ""}${duration ? " ore - " : ""}${period}`,
        projectManager: "Luca Caffi",
        deliveryMode: deliveryMode?.Descrizione,
        teachers: course?.NomeFormatore || course?.Formatore,
        platform: "",
        courseNotes: course?.Note,
        testDate: lastCourseDate?.isValid()
          ? lastCourseDate.format("DD/MM/YYYY")
          : "",
        ...test,
        participants: participantValues,
      },
      lucaCaffiSignature,
    );

    openPdfDocument(
      doc,
      `verbale_verifica_finale_corso_${course?.ID || ""}.pdf`,
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Verbale della verifica finale</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          I dati inseriti qui servono esclusivamente a generare il PDF e non
          vengono salvati in Koala.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Numero domande"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={test.questionCount}
              onChange={(event) => updateQuestionCount(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Soglia minima"
              type="number"
              helperText="Numero minimo di risposte corrette"
              inputProps={{
                min: 0,
                max: test.questionCount || undefined,
                step: 1,
              }}
              value={test.threshold}
              onChange={(event) =>
                updateTest(
                  "threshold",
                  clampToRange(
                    event.target.value,
                    0,
                    test.questionCount === ""
                      ? Infinity
                      : Number(test.questionCount),
                  ),
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>Tipologia test</InputLabel>
              <Select
                value={test.testType}
                label="Tipologia test"
                onChange={(event) => updateTest("testType", event.target.value)}
              >
                <MenuItem value="multiple">Risposta multipla</MenuItem>
                <MenuItem value="true-false">Vero/Falso</MenuItem>
                <MenuItem value="other">Altro</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Luogo/modalità verifica (facoltativo)"
              value={test.testLocation}
              onChange={(event) =>
                updateTest("testLocation", event.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Luogo del verbale (facoltativo)"
              value={test.documentPlace}
              onChange={(event) =>
                updateTest("documentPlace", event.target.value)
              }
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          Partecipanti
        </Typography>
        {participantValues.map((participant, index) => (
          <Grid
            container
            spacing={1}
            key={participant.id ?? index}
            sx={{ mb: 1, alignItems: "flex-start" }}
          >
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Partecipante"
                value={participant.name}
                disabled
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Presenza minima</InputLabel>
                <Select
                  value={participant.minimumAttendance}
                  label="Presenza minima"
                  onChange={(event) =>
                    updateParticipant(
                      index,
                      "minimumAttendance",
                      event.target.value,
                    )
                  }
                >
                  <MenuItem value="yes">Sì</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                required
                label="Punteggio"
                type="number"
                helperText={
                  test.questionCount ? `su ${test.questionCount} domande` : ""
                }
                inputProps={{
                  min: 0,
                  max: test.questionCount || undefined,
                  step: 1,
                }}
                value={participant.score}
                onChange={(event) =>
                  updateParticipant(
                    index,
                    "score",
                    clampToRange(
                      event.target.value,
                      0,
                      test.questionCount === ""
                        ? Infinity
                        : Number(test.questionCount),
                    ),
                  )
                }
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Esito automatico"
                value={
                  participant.score === "" || test.threshold === ""
                    ? ""
                    : participant.minimumAttendance === "yes" &&
                        Number(participant.score) >= Number(test.threshold)
                      ? "Idoneo"
                      : "Non idoneo"
                }
                disabled
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Note"
                value={participant.notes}
                onChange={(event) =>
                  updateParticipant(index, "notes", event.target.value)
                }
              />
            </Grid>
          </Grid>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button
          variant="contained"
          className="ButtonMiller"
          onClick={generatePdf}
        >
          Genera PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}
